import React from 'react';
import Dropdowns from '../../components/Dropdowns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useCustomerDataFilters } from 'contexts/CustomerDataFiltersContext';
import { fetchBranchData, BranchDataResponse, BranchData } from './api/index';
import BranchCard from './components/BranchCard';
import { cn } from 'lib/utils';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';


interface BranchDataPageProps {
  customerCode: string;
  customerName: string;
}

const BranchDataPage: React.FC<BranchDataPageProps> = ({ customerCode, customerName }) => {
  const lastQueryRef = React.useRef<string | null>(null);
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { years, year, levels, loading, error, handleSelectChange, handleBatchSelect, handleYearChange, getCurrentFilters } = useCustomerDataFilters();
  
  // Branch data state
  const [branchData, setBranchData] = React.useState<BranchData[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [dataLoading, setDataLoading] = React.useState<boolean>(false);
  const [dataError, setDataError] = React.useState<string>('');
  
  // UI state
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');
  const [sortBy] = React.useState<string>('total_income');
  const [sortDir] = React.useState<'asc' | 'desc'>('desc');
  const [page, setPage] = React.useState<number>(1);
  const [pageSize] = React.useState<number>(20);
  
  // Smooth loading states
  const { isLoading: smoothDataLoading, showContent: showDataContent } = useSmoothLoading(dataLoading, {
    minLoadingTime: 200,
    debounceTime: 200
  });
  


  const fetchData = React.useCallback(async (search?: string, currentPage = 1) => {
    if (!year || !customerCode) return;
    const filters = getCurrentFilters();

    const queryKey = JSON.stringify({
      year,
      filters,
      customer_code: customerCode,
      search: search?.trim() || '',
      page: currentPage,
      page_size: pageSize,
      sortBy,
      sortDir,
    });
  
    // Dedupe: if this exact query was already fetched, skip
    if (lastQueryRef.current === queryKey) {
      return;
    }
    lastQueryRef.current = queryKey;

    setDataLoading(true);
    setDataError('');
    try {
      const data = await fetchBranchData({
        year,
        filters,
        customer_code: customerCode,
        ...(search && search.trim() && { search: search.trim() }),
        sort: {
          by: sortBy,
          dir: sortDir,
        },
        page: currentPage,
        page_size: pageSize,
      });
      setBranchData(data.branches);
      setTotal(data.total);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch branch data';
      setDataError(message);
      if (isForbidden) {
        setBranchData([]);
        setTotal(0);
      }
    } finally {
      setDataLoading(false);
    }
  }, [getCurrentFilters, year, customerCode, sortBy, sortDir, pageSize]);

  React.useEffect(() => {
    if (!year || !customerCode) return;
    if (levels.length === 0) return;
  
    setPage(1);
    fetchData(searchTerm, 1);
  }, [year, levels, customerCode, fetchData, searchTerm]);
  

  React.useEffect(() => {
    setBreadcrumbs([
      { label: 'Reports', href: '#' },
      { label: 'Monthly Customer Data', href: '/reports/customer-data' },
      { label: 'Branch/Project Data' },
    ]);
  }, [setBreadcrumbs]);

  React.useEffect(() => {
    if (!year) return;
    setBranchData([]);
    setTotal(0);
    setPage(1);
    setSearchTerm(''); // Reset search when year changes
    lastQueryRef.current = null;  
  }, [year]);

  

  const handleSearch = React.useCallback((search: string) => {
    setPage(1);
    fetchData(search, 1);
  }, [fetchData]);

  const debouncedSearchRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

  const debouncedSearch = React.useCallback((search: string) => {
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }
    debouncedSearchRef.current = setTimeout(() => {
      handleSearch(search);
    }, 300);
  }, [handleSearch]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }
    };
  }, []);

  const handlePageChange = React.useCallback((newPage: number) => {
    setPage(newPage);
    fetchData(searchTerm, newPage);
  }, [fetchData, searchTerm]);

  const handleBackToCustomers = React.useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBackToCustomers}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold text-gray-900">
         {customerName}
        </h1>
      </div>

      {/* Filters Section */}
      <div className="space-y-4">
        <Dropdowns
          year={year}
          years={years}
          levels={levels}
          loading={loading}
          onSelectChange={handleSelectChange}
          onBatchSelect={handleBatchSelect}
          onYearChange={handleYearChange}
        />

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Search and Controls Section */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search for a branch..."
            value={searchTerm}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchTerm(newValue);
              debouncedSearch(newValue);
            }}
            className="pl-10"
          />
        </div>

        {/* Conversion Type Buttons */}
        <div className="flex gap-2">
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              conversionType === 'INR' 
                ? 'bg-black text-white' 
                : 'bg-white border border-black hover:bg-gray-200'
            }`}
            onClick={() => setConversionType('INR')}
          >
            INR
          </button>
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              conversionType === 'Lakhs' 
                ? 'bg-black text-white' 
                : 'bg-white border border-black hover:bg-gray-200'
            }`}
            onClick={() => setConversionType('Lakhs')}
          >
            Lakhs
          </button>
          <button 
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              conversionType === 'Crores' 
                ? 'bg-black text-white' 
                : 'bg-white border border-black hover:bg-gray-200'
            }`}
            onClick={() => setConversionType('Crores')}
          >
            Crores
          </button>
        </div>
      </div>

      {/* Data Section */}
      {dataError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">{dataError}</p>
        </div>
      )}

      {smoothDataLoading && (
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-white rounded-lg border shadow-sm">
              <div className="bg-gray-200 animate-pulse h-16 rounded-t-lg" />
              <div className="p-6 space-y-4">
                <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/2" />
                <div className="h-4 bg-gray-200 animate-pulse rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!smoothDataLoading && branchData.length === 0 && !dataError && (
        <div className="text-center py-12">
          <p className="text-gray-500">No branch data found. Try adjusting your filters or search terms.</p>
        </div>
      )}

      {!smoothDataLoading && branchData.length > 0 && showDataContent && (
        <div className={`space-y-6 transition-opacity duration-300 ${showDataContent ? 'opacity-100' : 'opacity-0'}`}>
          {/* Results Summary */}
          <div className="text-sm text-gray-600">
            Showing {branchData.length} of {total} branches
            {searchTerm && ` for "${searchTerm}"`}
          </div>

          {/* Branch Cards */}
          <div className="space-y-6">
            {branchData.map((branch) => (
              <BranchCard
                key={branch.ou_code}
                branch={branch}
                conversionType={conversionType}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchDataPage;
